import {
    categoryId,
    categoryTitle,
    couponOrigin,
    couponStatus,
    easiwareThemes,
    ecommerceType,
    productFilters
} from './const'
import type { Offer, ThemeShortDTO, Coupon } from 'shared/types'
import type { Geoloc } from './types'
import type { IntlShape } from 'react-intl'
import type { DeliveryParamsType } from 'shared/utils/ecommerce'
import { isDeliveryOrderCancelled } from 'shared/utils/ecommerce/utils'
import moment from 'moment'
//$FlowFixMe
import 'moment/locale/fr'
import type { CategoryInfoDTO, FilterShortDTO } from 'shared/types'
import type { Ingredient } from '../store/ecommerce/catalog'
import { mapPickUpTypeToOrderType } from '../store/ecommerce/catalog'

const { EASIWARE_DELIVERY_IN_APP } = easiwareThemes
const { DELIVERY_IN_APP } = ecommerceType
const { KINGDOM_POINTS } = couponOrigin

const timeout = (delay: number) => {
    //$FlowFixMe
    return new Promise(res => setTimeout(res, delay))
}

const toSrcSet = (urls: Array<string>) => {
    if (!urls) {
        return
    }
    urls = Array.isArray(urls) ? urls : [urls]
    return urls
        .filter(url => (url ? true : false))
        .map((url, index) => `${url} ${index + 1}x`)
        .join(', ')
}

const getTwicPicsSource = (urls?: Array<string>, twicPicsDomain: string) => {
    if (!urls || urls.length === 0) {
        return
    }
    const url = urls[0]
    return url.slice(url.indexOf(twicPicsDomain) + twicPicsDomain.length)
}

const updateFiltersArray = (
    filters: Array<string>,
    code: string,
    unique?: boolean
) => {
    const index = filters.indexOf(code)
    if (index !== -1) {
        filters.splice(index, 1)
    } else if (!unique) {
        filters.push(code)
    } else {
        filters = [code]
    }
    return filters
}

/**
 * Checks if a given date has passed
 * @param date Given date whose temporality needed to be checked
 * @returns {boolean} The date has passed (true) or not (false)
 */
const isPastedDate = (date: string) => {
    const now = new Date()
    const selectedDate = new Date(date)
    return selectedDate < now
}

/**
 * Formats a date from "dd/mm/yyyy" or "yyyy-MM-dd" to "ddMMyyyy"
 * @param date The date in "dd/MM/yyyy" or "yyyy-MM-dd" formats
 * @returns {string} The date in format "ddMMyyyy"
 */
const formatDate = (date: string) => {
    if (date.includes('/')) {
        const dateToPost = date.split('/')
        return `${dateToPost[0]}${dateToPost[1]}${dateToPost[2]}`
    } else {
        const dateToPost = date.split('-')
        return `${dateToPost[2]}${dateToPost[1]}${dateToPost[0]}`
    }
}

const formatKingdomCode = (code?: string) => {
    if (!code) {
        return ''
    }

    return code.length > 6 ? code.replace(/(.{3})/g, '$1 ') : code
}

const getDayName = (date: Date) => {
    const dayName = [
        'ZERO',
        'SUNDAY',
        'MONDAY',
        'TUESDAY',
        'WEDNESDAY',
        'THURSDAY',
        'FRIDAY',
        'SATURDAY'
    ]
    return dayName[date.getDay() + 1]
}

const getMonthName = (date: Date) => {
    const monthName = [
        'ZERO',
        'JANUARY',
        'FEBRUARY',
        'MARCH',
        'APRIL',
        'MAY',
        'JUNE',
        'JULY',
        'AUGUST',
        'SEPTEMBER',
        'OCTOBER',
        'NOVEMBER',
        'DECEMBER'
    ]
    return monthName[date.getMonth() + 1]
}

/**
 * Formats a date from any Date castable format to "dd Month yyyy" (ex: "22 Juillet 2022")
 * @param date The date to be formatted
 * @param intl Internationalizer
 * @returns {string} The date in the format "dd Month yyyy"
 */
const getFullDate = (date: string, intl: IntlShape) => {
    const newDate = new Date(date)
    const day = newDate.getDate()
    const month = intl.formatMessage({ id: getMonthName(newDate) })
    const year = newDate.getFullYear()
    return day + ' ' + month + ' ' + year
}

/**
 * Formats a date from any Date castable format to "Weekday dd Month" (ex: "Vendredi 22 Juillet")
 * @param date The date to be formatted
 * @param intl Internationalizer
 * @returns {string} The date in the format "Weekday dd Month"
 */
const getShortDate = (date: string, intl: IntlShape) => {
    const newDate = new Date(date)
    const weekday = intl.formatMessage({ id: getDayName(newDate) })
    const day = newDate.getDate()
    const month = intl.formatMessage({ id: getMonthName(newDate) })
    return weekday + ' ' + day + ' ' + month
}

/**
 * Formats a dateTime from "yyyy-MM-dd'T'HH:mm:ss" to "dd/MM/yyyy HH:mm:ss"
 * @param dateTime The dateTime in "yyyy-MM-dd'T'HH:mm:ss" format
 * @returns {string} The dateTime in "dd/MM/yyyy HH:mm:ss" format
 */
const formatDateTime = (dateTime: string) => {
    if (dateTime.includes('-')) {
        let dateTimeToPost = dateTime.split('T')
        const date = dateTimeToPost[0].split('-')
        return `${date[2]}/${date[1]}/${date[0]} ${dateTimeToPost[1]}`
    }
    return dateTime
}

/**
 * Formats a dateTime from "yyyy-MM-dd'T'HH:mm:ss" to "dd/MM/yyyy à {HH}h{mm}"
 * @param dateTime The dateTime in "yyyy-MM-dd'T'HH:mm:ss" format
 * @returns {string} The dateTime in "dd/MM/yyyy à {HH}h{mm}" format
 */
const formatDateAndTime = (dateTime: string) => {
    if (dateTime.includes('-')) {
        let dateTimeToPost = dateTime.split('T')
        let hour = dateTimeToPost[1].split(':')[0]
        let minutes = dateTimeToPost[1].split(':')[1]
        const date = dateTimeToPost[0].split('-')
        return `${date[2]}/${date[1]}/${date[0]} à ${hour}h${minutes}`
    }
    return dateTime
}

/**
 * Formats a dateTime from any Date castable format to "dd/MM/yyyy"
 * @param dateTime The dateTime from any DateTime castable format to be formatted
 * @returns {*} Nothing or the dateTime in "dd/MM/yyyy" format
 */
const formatDateToDayMonthYear = (dateTime?: string) => {
    if (!dateTime) {
        return
    }
    moment.locale('fr')
    return moment(dateTime).format('L')
}

/**
 * Formats a dateTime from any Date castable format to "dd Month"
 * @param dateTime The dateTime from any DateTime castable format to be formatted
 * @returns {*} Nothing or the dateTime in "dd Month" format
 */
const formatDateToDayAndMonth = (dateTime?: string) => {
    if (!dateTime) {
        return
    }
    moment.locale('fr')
    return moment(dateTime).format('D MMMM')
}

/**
 * Formats a dateTime from any Date castable format to "HH:mm"
 * @param dateTime The dateTime from any DateTime castable format to be formatted
 * @returns {*} Nothing or the dateTime in "HH:mm" format
 */
const formatDateToHoursAndMinutes = (dateTime?: string) => {
    if (!dateTime) {
        return
    }
    moment.locale('fr')
    return moment(dateTime).format('LT')
}

/**
 * Formats a time expressed in minutes (number) to a time in "{HH} h {mm} min" or "{mm} min"
 * @param timeInMin Number of minutes to be formatted
 * @returns {string} A time in "{HH} h {mm} min" (if timeInMin < 60) or "{mm} min" (else) formats
 */
const formatTime = (timeInMin: number) => {
    if (timeInMin / 60 >= 1) {
        const hours = (timeInMin / 60).toFixed(0)
        const minutes = (timeInMin % 60) + 1
        return (
            hours.toString() +
            ' h ' +
            Number(minutes.toFixed(0)).toString() +
            ' min'
        )
    } else {
        //$FlowFixMe
        return (Number(timeInMin?.toFixed(0)) + 1).toString() + ' min'
    }
}

/**
 * Adds a number of seconds to current time
 * @param seconds Number of seconds to be added
 * @returns {undefined | string} Undefined or the current time with the number of seconds added
 */
const addSecondsToCurrentTime = (seconds?: number) => {
    if (!seconds) {
        return
    }
    return moment().add(seconds, 'seconds')
}

/**
 * Adds a number of seconds to a given time
 * @param time Given time to add seconds
 * @param seconds Number of seconds to be added
 * @returns {undefined | string} Undefined or the given time with the number of seconds added
 */
const addSecondsToGivenTime = (time: string, seconds?: number) => {
    if (!seconds) {
        return
    }
    return moment(time).add(seconds, 'seconds')
}

/**
 * Subtracts a number of seconds to a given time
 * @param time Given time to subtract seconds
 * @param seconds Number of seconds to be subtracted
 * @returns {undefined | string} Undefined or the given time with the number of seconds subtracted
 */
const subtractSecondsToGivenTime = (time: string, seconds?: number) => {
    if (!seconds) {
        return
    }
    return moment(time).subtract(seconds, 'seconds')
}

/**
 * Checks if a given time value is before a given date
 * @param value Given time value to check
 * @param date Given date to check
 * @returns {boolean} The given time is before the given date (true) or not (false)
 */
const isValueBeforeDate = (value: string, date: string) => {
    let dateFormatted = date
    if (date.includes('/')) {
        dateFormatted = date.split('/')
        dateFormatted = `${dateFormatted[2]}-${dateFormatted[1]}-${dateFormatted[0]}`
    }
    const dateFormattedTime = new Date(dateFormatted).getTime()
    const valueToTime = new Date(value).getTime()
    return valueToTime < dateFormattedTime
}

const isAllowedToSeeStudentFeature = (
    birthDate: string,
    studentLimitAge: number,
    format: string = 'DD/MM/YYYY'
) =>
    moment(birthDate, format).isAfter(
        moment().subtract(studentLimitAge, 'year')
    )

/**
 * Checks with the birthdate given if the user is too young or requires parental consent
 * @param date Given user birthdate
 * @param minAge Minimal age to use the application
 * @returns boolean checking if user is too young
 */
const isCorrectBirthdate = (date: string, minAge: number) => {
    const currentDate = new Date()
    const minimumYearOfBirth = currentDate.setFullYear(
        currentDate.getFullYear() - minAge
    )
    const selectedDate = new Date(date)
    return selectedDate < new Date(minimumYearOfBirth)
}

/**
 * Checks if a given date is between the current date minus {min} years and the current date minus {max} years
 * @param date The date whose temporality we want to check
 * @param min The min years before the current date
 * @param max The max years before the current date
 * @returns {boolean} The date is in the interval (true) or not (false)
 */
const isDateBetween = (date: string, min: number, max: number) => {
    const nowMin = new Date()
    const nowMax = new Date()
    const maxYearsBefore = nowMax.setFullYear(nowMax.getFullYear() - max)
    const minYearsBefore = nowMin.setFullYear(nowMin.getFullYear() - min)
    const selectedDate = new Date(date)
    return (
        selectedDate > new Date(maxYearsBefore) &&
        selectedDate < new Date(minYearsBefore)
    )
}

/**
 * Checks if a given time is (strictly or not) over {minHour} time and (strictly or not) under {maxHour} time
 * @param time The time whose temporality we want to check
 * @param minHour The min of comparison interval
 * @param maxHour The max of comparison interval
 * @param strictlyOver Strictly over comparison
 * @param strictlyUnder Strictly under comparison
 * @returns {boolean} The time is in the interval (true) or not (false)
 */
const isTimeBetween = (
    time: string,
    minHour: string,
    maxHour: string,
    strictlyOver?: boolean,
    strictlyUnder?: boolean
) => {
    const selectedTime = setHours(time)
    const nowMin = setHours(minHour)
    const nowMax = setHours(maxHour)
    return (
        (strictlyOver ? selectedTime > nowMin : selectedTime >= nowMin) &&
        (strictlyUnder ? selectedTime < nowMax : selectedTime <= nowMax)
    )
}

/**
 * Gives the number of days between two given dates
 * @param date1 First given date
 * @param date2 Second given date
 * @returns {number} Number of days between the two given dates
 */
const dateDifferenceInDays = (date1: string | Date, date2: string | Date) => {
    const millisecondsPerDay = 24 * 60 * 60 * 1000
    return Math.round(
        Math.abs(new Date(date2) - new Date(date1)) / millisecondsPerDay
    )
}

/**
 * Gives the number of hours between two given dates
 * @param date1 First given date
 * @param date2 Second given date
 * @returns {number} Number of hours between the two given dates
 */
const timeDifferenceBetweenDatesInHours = (
    date1: string | Date,
    date2: string | Date
) => {
    return (
        Math.abs(new Date(date2).getTime() - new Date(date1).getTime()) /
        3600000
    )
}

/**
 * Sets hours for a given date or the current date
 * @param time Number of hours to set to the given/current date
 * @param date Given date to be added with the number of hours
 * @returns {Date} The given/current date added with number of hours
 */
const setHours = (time: string, date?: Date) => {
    const parsedTime = time.split(':')
    const updatedDate = date ? new Date(date) : new Date()
    updatedDate.setHours(Number(parsedTime[0]), Number(parsedTime[1]))
    return updatedDate
}

/**
 * Formats a given date to "dd/MM/yyyy"
 * @param date The given date to be formatted
 * @returns {string} The given date in "dd/MM/yyyy" format
 */
const formatDateToDisplay = (date: Date) => {
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yyyy = date.getFullYear()
    return dd + '/' + mm + '/' + yyyy
}

/**
 * Formats a given date to "yyyy-MM-ddThh:mm"
 * @param date The given date to be formatted
 * @returns {string} The given date in "yyyy-MM-ddThh:mm" format
 */
const formatDateObjectToFieldDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return year + '-' + month + '-' + day + 'T' + hours + ':' + minutes
}

/**
 * Converts a given date from "yyyy-MM-ddThh:mm" to a new Date object
 * @param fieldDate The given date to convert
 * @returns {Date} The new Date object obtained from the given date
 */
const formatFieldDateToDateObject = (fieldDate: string) => {
    const [date, time] = fieldDate.split('T')
    const [year, month, day] = date.split('-')
    const [hour, minute] = time.split(':')
    return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute)
    )
}

/**
 * Converts a given date from "dd/MM/yyyy" to a new Date object
 * @param displayDate The given date to convert
 * @returns {Date} The new Date object obtained from the given date
 */
const formatDisplayDateToDate = (displayDate: string) => {
    const [day, month, year] = displayDate.split('/')
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
}

const getStatusProps = (status: string) => {
    switch (status) {
        case couponStatus.BURNED:
            return {
                opacity: 0.5,
                translation: 35,
                enable: false
            }
        case couponStatus.EXPIRED:
        case couponStatus.UNAVAILABLE:
            return {
                opacity: 0.5,
                translation: 62,
                enable: false
            }
        case couponStatus.VALID:
        default:
            return {
                opacity: 1,
                translation: 0,
                enable: true
            }
    }
}

const splitString = (label: string, breakLength: number) => {
    let startLabel = label
    let endLabel = ''
    const splitLabelBySpace = label.split(' ')
    splitLabelBySpace.reverse().some((word: string) => {
        const wordIndex = startLabel.lastIndexOf(word) - 1
        startLabel = startLabel.substring(0, wordIndex)
        endLabel = label.substring(wordIndex + 1)
        return wordIndex <= breakLength
    })
    return {
        startLabel,
        endLabel
    }
}

const getPath = (selectedTheme: ThemeShortDTO) => {
    if (selectedTheme.order) {
        return 'order'
    } else if (selectedTheme.restaurant) {
        return 'restaurant'
    } else if (selectedTheme.notation) {
        return 'note'
    } else if (selectedTheme.kingdom) {
        return 'kingdom'
    }
    return ''
}

const deleteCookies = () => {
    document.cookie.split(';').forEach((c: any) => {
        document.cookie = c
            .replace(/^ +/, '')
            .replace(
                /[=].*/,
                '=;expires=' + new Date().toUTCString() + ';path=/'
            )
    })
}

const getCurrentPosition = (props: Geoloc) => {
    const {
        applyFilters,
        filters,
        hasSearchBeenMade,
        searchNearUserPosition,
        address,
        setUserPosition,
        geolocation
    } = props
    if (geolocation) {
        geolocation.getCurrentPosition(
            (p: Position) => {
                const coords = {
                    lat: p.coords.latitude,
                    lng: p.coords.longitude
                }
                setUserPosition && setUserPosition(coords)
                if (!hasSearchBeenMade && searchNearUserPosition) {
                    searchNearUserPosition({
                        coords,
                        address,
                        filters
                    })
                }
            },
            (e: PositionError) => {
                console.error('Error getting position: ', e)
                filters &&
                    filters.length > 0 &&
                    applyFilters &&
                    applyFilters(filters)
            },
            {
                enableHighAccuracy: true,
                timeout: 30000,
                maximumAge: 1000 * 60 * 10
            }
        )
    }
}

const distance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371e3 // Radius of the earth in meters
    const dLat = (lat2 - lat1) * (Math.PI / 180)
    const dLon = (lon2 - lon1) * (Math.PI / 180)
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) *
            Math.cos(lat2 * (Math.PI / 180)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c
}

const formatPrice = (price: number, defaultMessage?: string) => {
    return price || price === 0
        ? price.toLocaleString('fr-FR', {
              style: 'currency',
              currency: 'EUR'
          })
        : defaultMessage || ''
}

const concatWithComma = (initialString: string, stringToConcate: string) => {
    initialString.length === 0
        ? (initialString += stringToConcate)
        : (initialString += ',' + stringToConcate)
    return initialString
}

const round = (number: number, fractionDigits: number): number =>
    Number(Number(number).toFixed(fractionDigits))

const isDefined = (value: any) => value !== null && value !== undefined

const isEmptyString = (value: string) => value === ''

const calculateMinutesFromNowToTime = (time?: string) => {
    //Calculates time in minutes between the currentTime and a targetTimewithin 24 hours
    //Time format must be a string with the form : HHhMM
    if (!time) {
        return undefined
    }
    const currentHour = new Date().getHours()
    const currentMinute = new Date().getMinutes()
    const targetTimeTab = time.split('h')
    //If the currentHour is bigger then the targetHour -> targetHour = targetHour + 24
    const targetHour =
        parseInt(targetTimeTab[0]) < currentHour
            ? parseInt(targetTimeTab[0]) + 24
            : parseInt(targetTimeTab[0])
    const targetMinute = parseInt(targetTimeTab[1])
    const hourDifference = targetHour - currentHour
    const minDifference = targetMinute - currentMinute
    return hourDifference * 60 + minDifference
}

const getAddressWithoutCountry = (address: string) => {
    const splitAddress = address.split(',')
    if (!!splitAddress[1]) {
        return splitAddress[0] + ',' + splitAddress[1]
    }
    return splitAddress[0]
}

const shouldDisplayETA = (
    deliveryParams?: DeliveryParamsType,
    discarded?: boolean,
    orderStatus?: string
) => {
    if (!!deliveryParams) {
        const {
            deliveryStatus,
            higherArrivalRange,
            lowerArrivalRange,
            higherDeliveryArrivalRange,
            lowerDeliveryArrivalRange
        } = deliveryParams

        const isCancelled = isDeliveryOrderCancelled(
            deliveryStatus,
            orderStatus
        )
        const hasArrivalRange =
            isDefined(higherArrivalRange) && isDefined(lowerArrivalRange)
        const hasDeliveryArrivalRange =
            isDefined(higherDeliveryArrivalRange) &&
            isDefined(lowerDeliveryArrivalRange)
        return (
            !isCancelled &&
            !discarded &&
            (hasDeliveryArrivalRange || hasArrivalRange)
        )
    }
}

const getEasiwareTheme = (
    selectedTheme: ?ThemeShortDTO,
    ecommerceMode?: string
) => {
    if (selectedTheme?.ecommerce && ecommerceMode === DELIVERY_IN_APP) {
        return EASIWARE_DELIVERY_IN_APP
    }
    if (selectedTheme?.easiwareId) {
        return selectedTheme?.easiwareId
    }
    return undefined
}

const isPickupTypeAvailable = (item: Offer, pickUpType?: string) => {
    if (!pickUpType) return false
    if (!item.orderTypeAvailabilities) return true
    if (item.orderTypeAvailabilities) {
        return item.orderTypeAvailabilities.includes(
            mapPickUpTypeToOrderType(pickUpType)
        )
    }
    return false
}

/**
 * sort an offers array with promo first and by ascending order
 * @param offers offer array to sort
 * @param userPoints user points
 * @param minPoint level points
 * @returns {unLockedOffers,lockedOffers, pointsBeforeLevelUnlocked} offers arrary sorted
 */
const sortOffersInKingdomLevel = (
    offers: Offer[],
    userPoints: number,
    minPoints: number,
    pickupType?: string
) => {
    let unLockedOffers: Offer[] = []
    let lockedOffers: Offer[] = []
    let pointsBeforeLevelUnlocked
    const isInPromotion = (offer: Offer) => {
        return (
            !!offer.pointsBeforeDiscount &&
            isPickupTypeAvailable(offer, pickupType)
        )
    }

    if (offers) {
        offers.sort((offer1, offer2) => {
            if (isInPromotion(offer1) !== isInPromotion(offer2)) {
                return isInPromotion(offer1) ? -1 : 1
            }
            return isInPromotion(offer1) && offer1.points && offer2.points
                ? offer1.points - offer2.points
                : 0
        })
        offers.forEach(offer => {
            if (
                isInPromotion(offer)
                    ? !!offer.points && userPoints >= offer.points
                    : userPoints >= minPoints
            ) {
                unLockedOffers.push(offer)
            } else {
                lockedOffers.push(offer)
            }
        })

        const lockedPromotions =
            lockedOffers.length !== 0
                ? lockedOffers
                      .filter(offer => isInPromotion(offer) && !!offer.points)
                      .map(offer => offer.points)
                : []

        pointsBeforeLevelUnlocked =
            lockedPromotions.length === 0 || lockedOffers.length === 0
                ? minPoints - userPoints
                : Math.min(...lockedPromotions) - userPoints
    }
    return {
        unLockedOffers,
        lockedOffers,
        pointsBeforeLevelUnlocked
    }
}

const displayMeatOrigin = (
    filters: Array<FilterShortDTO>,
    category: CategoryInfoDTO
) => {
    const isVeggieOrBakery = !!filters
        ? filters.some(filter =>
              [productFilters.CIRCLE_VEGGIE, productFilters.BAKERY].includes(
                  filter.code
              )
          )
        : false

    const hasMeat = !!filters
        ? filters.some(filter =>
              [
                  productFilters.CIRCLE_BEEF,
                  productFilters.CIRCLE_CHICKEN,
                  productFilters.CIRCLE_FISH
              ].includes(filter.code)
          )
        : false

    const isDessertsOrDrinks =
        [
            categoryId.DRINKS,
            categoryId.DRINKS_CC,
            categoryId.HOT_DRINKS,
            categoryId.DESSERTS,
            categoryId.SWEET_CORNER
        ].includes(category?.categoryId) ||
        [categoryTitle.DESSERTS, categoryTitle.SAUCES].includes(category?.title)

    const isIce =
        !!filters && filters.some(filter => filter.code === productFilters.ICE)

    return !isDessertsOrDrinks && !isIce && (hasMeat || !isVeggieOrBakery)
}

const filterGroupsOfIngredientsBySingleChoice = (
    ingredientsGroup: Array<Ingredient>
) => {
    let isUnique = false
    const filteredIngredientsGroup: Array<Ingredient> = ingredientsGroup.filter(
        ingr => ingr.quantityMin === 1 && ingr.quantityMax === 1
    )
    isUnique = filteredIngredientsGroup.length > 0
    return { isUnique, ingredients: filteredIngredientsGroup }
}

const hasCartExpired = (lastTimeCartModified: number) => {
    const lastTimeCartModifiedDate = moment(lastTimeCartModified)
    const cartHasBeenModifiedToday = lastTimeCartModifiedDate.isSame(
        moment(),
        'day'
    )
    const format = 'hh:mm:ss'
    const beforeTime = moment('00:01:00', format)
    const afterTime = moment('05:00:00', format)

    // Cart has been modified the day before or before 5am the same day and it's after 5am
    if (
        !cartHasBeenModifiedToday ||
        (cartHasBeenModifiedToday &&
            lastTimeCartModifiedDate.isBetween(beforeTime, afterTime) &&
            moment().isAfter(afterTime, 'hour'))
    )
        return true
    else return false
}

const filterKingdomPointsCoupons = (coupons: Coupon[]) => {
    //$FlowFixMe
    return coupons?.filter(coupon => coupon?.origin !== KINGDOM_POINTS)
}

export {
    timeout,
    deleteCookies,
    getPath,
    toSrcSet,
    updateFiltersArray,
    isPastedDate,
    isDateBetween,
    isTimeBetween,
    isValueBeforeDate,
    isCorrectBirthdate,
    dateDifferenceInDays,
    timeDifferenceBetweenDatesInHours,
    getStatusProps,
    getCurrentPosition,
    distance,
    formatDate,
    getDayName,
    getMonthName,
    getFullDate,
    getShortDate,
    getTwicPicsSource,
    formatKingdomCode,
    formatDateTime,
    formatDateAndTime,
    formatDateToDayMonthYear,
    formatDateToDayAndMonth,
    formatDateToHoursAndMinutes,
    splitString,
    formatPrice,
    formatTime,
    addSecondsToCurrentTime,
    addSecondsToGivenTime,
    subtractSecondsToGivenTime,
    formatDateToDisplay,
    formatDateObjectToFieldDate,
    formatFieldDateToDateObject,
    formatDisplayDateToDate,
    concatWithComma,
    round,
    isDefined,
    isEmptyString,
    calculateMinutesFromNowToTime,
    getAddressWithoutCountry,
    shouldDisplayETA,
    setHours,
    getEasiwareTheme,
    isAllowedToSeeStudentFeature,
    sortOffersInKingdomLevel,
    displayMeatOrigin,
    filterGroupsOfIngredientsBySingleChoice,
    hasCartExpired,
    filterKingdomPointsCoupons
}
